import { Controller } from '@hotwired/stimulus';
import { readGridFields, writeGridFields } from '../lib/grid_detection/fields.js';

// Owns only the grid dialog. The worker never touches DOM or persists settings.
export default class extends Controller {
  static targets = ['fields', 'progress', 'message', 'detect', 'manual', 'detectionWarning', 'layersWarning'];
  static values = { stateUrl: String, applyUrl: String, settingsUrl: String, imageId: String, auto: Boolean };

  connect() {
    this.connected = true;
    this.closing = () => this.cancel();
    this.frame = this.element.closest('dialog')?.closest('turbo-frame') || this.element.closest('turbo-frame');
    this.frame?.addEventListener('modal:closing', this.closing);
    const { detail } = this.dispatch('ready', { detail: { imageId: this.imageIdValue } });
    if (detail.draft) {
      writeGridFields(this.element, detail.draft);
      this.syncWarning(detail.draft);
      this.messageTarget.textContent = 'Grid settings restored. Apply grid to save any changes.';
    }
    if (this.hasLayersWarningTarget && typeof detail.gridDependentLayers === 'boolean')
      this.layersWarningTarget.hidden = !detail.gridDependentLayers;
    if (this.autoValue) {
      this.autoValue = false; // Do not restart when Turbo restores this dialog.
      this.detect();
    }
  }

  disconnect() {
    this.connected = false;
    this.frame?.removeEventListener('modal:closing', this.closing);
    this.cancel();
  }

  cancel() {
    this.request?.abort();
    this.request = null;
    this.worker?.terminate();
    this.worker = null;
    clearTimeout(this.timeout);
    this.busy(false);
  }

  busy(value) {
    this.fieldsTarget.hidden = value;
    this.fieldsTarget.disabled = value;
    this.progressTarget.hidden = !value;
    this.detectTarget.disabled = value;
    if (this.hasManualTarget) this.manualTarget.disabled = value || !this.imageIdValue;
    this.element.setAttribute('aria-busy', String(value));
  }

  manual(event) {
    this.cancel();
    const request = this.dispatch('manual', {
      detail: {
        values: readGridFields(this.fieldsTarget.querySelector('form')),
        imageId: this.imageIdValue,
        url: this.settingsUrlValue
      }
    });
    if (!request.defaultPrevented) {
      event.stopImmediatePropagation();
      this.messageTarget.textContent = 'Wait for the map image to load in the editor, then try again.';
    }
  }

  submitted(event) {
    if (event.detail.success) this.dispatch('updated', { detail: { url: this.stateUrlValue } });
  }

  syncWarning(grid) {
    if (this.hasDetectionWarningTarget)
      this.detectionWarningTarget.hidden = ![true, 'true', '1', 1].includes(grid.grid_detection_approximate);
  }

  async detect(event) {
    event?.preventDefault();
    this.cancel();
    const appearance = Object.fromEntries(
      Object.entries(readGridFields(this.fieldsTarget.querySelector('form'))).filter(([name]) =>
        ['grid_width', 'grid_color', 'grid_hidden'].includes(name)
      )
    );
    this.busy(true);
    this.messageTarget.textContent = '';
    const request = (this.request = new AbortController());
    this.timeout = setTimeout(() => {
      if (this.request !== request) return;
      this.cancel();
      this.messageTarget.textContent = 'Grid detection timed out. Adjust the grid manually or try again.';
    }, 120_000);
    try {
      const response = await fetch(this.stateUrlValue, {
        headers: { Accept: 'application/json' },
        signal: request.signal,
        cache: 'no-store'
      });
      if (!response.ok) throw new Error('Grid settings could not be loaded. Please reopen this dialog.');
      const state = await response.json();
      if (request.signal.aborted) return;
      this.syncWarning(state.grid);
      // Uploads can open this dialog before the underlying editor refreshes.
      this.dispatch('source', { detail: state });
      if (!state.supported)
        throw new Error('Grid detection supports JPEG, PNG, WebP and GIF images. Set this grid manually.');
      this.worker = this.createWorker();
      this.worker.onmessage = ({ data }) => this.detected(data, state, request, appearance);
      this.worker.onerror = () =>
        this.failed('Grid detection is unavailable. Adjust the grid manually or try again.', request);
      this.worker.postMessage({ url: state.image_url });
    } catch (error) {
      if (!request.signal.aborted) this.failed(error.message, request);
    }
  }

  createWorker() {
    return new Worker(new URL('../lib/grid_detection/worker.js', import.meta.url), { type: 'module' });
  }

  async detected({ result, error }, state, request, appearance) {
    if (!this.connected || request.signal.aborted || this.request !== request) return;
    this.worker?.terminate();
    this.worker = null;
    if (error) return this.failed(error, request);
    if (!result) return this.failed('No reliable grid found. Your grid settings have not changed.', request);
    try {
      const response = await fetch(this.applyUrlValue, {
        method: 'PATCH',
        signal: request.signal,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({
          snapshot: state.snapshot,
          game_map: {
            grid_size: result.grid_size,
            grid_offset_x: result.grid_offset_x,
            grid_offset_y: result.grid_offset_y,
            grid_detection_approximate: !!result.approximate
          }
        })
      });
      if (response.status === 409)
        throw new Error(
          'The image or grid settings changed during detection. Reopen settings or auto detect again to use the latest image.'
        );
      if (!response.ok) throw new Error('The detected grid could not be saved. Please try again.');
      const saved = await response.json();
      if (!this.connected || request.signal.aborted || this.request !== request) return;
      writeGridFields(this.element, { ...saved.grid, ...appearance });
      this.syncWarning(saved.grid);
      this.dispatch('source', { detail: saved });
      this.messageTarget.textContent = result.approximate
        ? "Approximate grid detected and saved. The image's lines vary slightly; adjust if needed."
        : 'Grid detected and saved. Adjust the settings if needed.';
      this.cancel();
    } catch (error) {
      if (!request.signal.aborted) this.failed(error.message, request);
    }
  }

  failed(message, request) {
    if (this.request !== request || !this.connected) return;
    this.cancel();
    this.messageTarget.textContent = message;
  }
}
